import NextImage from 'next/image'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useQueryClient } from 'react-query'

import { Button } from '@wartek-id/button-v1'
import type { DialogProps } from '@wartek-id/dialog-v1'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogOverlay,
} from '@wartek-id/dialog-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Spinner } from '@wartek-id/spinner'
import { Text } from '@wartek-id/text-v1'
import { Textarea } from '@wartek-id/textarea'
import { useToast } from '@wartek-id/toast'

import {
  useAPIGetFollowUpDocument,
  useAPIGetFollowUpReflectionDocument,
  useAPIPelaksanaanProgress,
  useAPIUpsertFollowUpReflectionDocument,
  API_GET_FOLLOW_UP_DOCUMENT_QUERY_KEY,
  API_PATH_PELAKSANAAN_PROGRESS,
  API_PATH_GET_FOLLOW_UP_REFLECTION_DOCUMENT,
} from 'api/kinerja/useAPIKinerja'
import {
  SectionCard,
  SectionCardHeader,
  SectionCardBody,
  SectionCardFooter,
} from 'app/KinerjaShared/components/SectionCard'
import { Header } from 'app/KinerjaShared/components/Header'
import { ConfirmationDialog } from 'app/KinerjaShared/components/ConfirmationDialog'
import { REFLEKSI_EVALUASI_TINDAK_LANJUT_CONTENT } from 'app/KinerjaShared/constants/praktikKinerja'
import { DUKUNGAN_UPAYA_TINDAK_LANJUT } from 'app/KinerjaShared/constants/upayaTindakLanjut'
import { PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import { generateErrorMessage, getErrorCode } from 'app/KinerjaShared/utils/api'
import { PageGuardPamongPelaksanaanStatus } from 'app/KinerjaPamongPage/components/pelaksanaan/PageGuardPamongPelaksanaanStatus'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { useSafeBackNavigation } from 'app/KinerjaShared/hooks/useSafeBackNavigation'
import { PRAKTIK_KINERJA_STEPS } from 'app/KinerjaPamongPage/constants/praktik-kinerja-steps'
import { assetUrl } from 'utils/url'
import { LangkahObservasiPraktikKinerjaSection } from 'app/KinerjaShared/components/LangkahObservasiPraktikKinerjaSection'
import { useBackNavigationConfirmation } from 'app/KinerjaShared/hooks/useBackNavigationConfirmation'

import type { CustomError } from 'utils/customError'
import type {
  IFollowUpReflectionDocumentForm,
  IFollowUpTargetItem,
  TStatePelaksanaan,
} from 'types/kinerja-pelaksanaan'
import type { MultiPeriodPayload } from 'types/kinerja'

type ActiveDialogTypes =
  | 'BELAJAR_LAINNYA'
  | 'BELAJAR_RGTK'
  | 'SAVE_TO_DRAFT'
  | 'SUBMIT'
  | 'UNSAVED'
  | 'NONE'

type RefleksiEvaluasiTindakLanjutProps = {
  periodParams?: MultiPeriodPayload
  ptkId?: string
}

const STEP: TStatePelaksanaan = 'DOCUMENT_REFLECTION_FOLLOW_UP'
const STEP_TITLE = PRAKTIK_KINERJA_STEPS[STEP].title

export function RefleksiEvaluasiTindakLanjut({
  periodParams,
  ptkId,
}: RefleksiEvaluasiTindakLanjutProps) {
  const router = useRouter()
  const toast = useToast()
  const queryClient = useQueryClient()
  const upsertMutation = useAPIUpsertFollowUpReflectionDocument({
    useErrorBoundary: false,
  })

  // -----------------------------------------
  // Handle redirect to Praktik Kinerja Hub page
  // -----------------------------------------
  const praktikKinerjaHubRoute = `${PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH}/pelaksanaan/praktik-kinerja`

  // Uses router.replace() directly to bypass beforePopState guard
  const handleGoToPraktikKinerjaHub = useCallback(() => {
    router.replace(praktikKinerjaHubRoute)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [praktikKinerjaHubRoute])

  // -----------------------------------------
  // Handle dialogs
  // -----------------------------------------
  const [activeDialog, setActiveDialog] = useState<ActiveDialogTypes>('NONE')

  const handleCloseDialog = () => {
    setActiveDialog('NONE')
  }

  // -----------------------------------------
  // Resolve period params
  // -----------------------------------------
  const { periodNo, periodYear } = usePeriodContext()

  const resolvedPeriodParams = useMemo(() => {
    if (periodParams) return periodParams

    // Default with current period
    return {
      skpPeriod: periodNo,
      skpYear: periodYear,
    }
  }, [periodParams, periodNo, periodYear])

  const formMethods = useForm<IFollowUpReflectionDocumentForm>({
    defaultValues: {
      additionalAchievement: '',
      additionalChallenge: '',
      additionalImprovementPlan: '',
      additionalInspiration: '',
    },
  })

  // -----------------------------------------
  // Get Pelaksanaan Progress data
  // -----------------------------------------
  const { data: dataPelaksanaanProgress } = useAPIPelaksanaanProgress({
    ptkId,
    tipe: 'SPNF',
    periodParams: {
      skp_period: resolvedPeriodParams.skpPeriod,
      skp_year: resolvedPeriodParams.skpYear,
    },
    opts: {
      staleTime: 60 * 60 * 1000, // 1 hour
    },
  })

  const isEvaluated =
    dataPelaksanaanProgress?.data?.evaluationDocumentState === 'READY'

  // -----------------------------------------
  // Get Rencana Tindak Lanjut data
  // -----------------------------------------
  const { data: followUpDocumentData, isLoading: isLoadingFollowUpDocument } =
    useAPIGetFollowUpDocument({
      params: { ptkId, tipe: 'SPNF' },
      periodParams: {
        skp_period: resolvedPeriodParams.skpPeriod,
        skp_year: resolvedPeriodParams.skpYear,
      },
      options: {
        useErrorBoundary: true,
        staleTime: 60 * 60 * 1000, // 1 hour
        retry: false,
      },
    })

  // -----------------------------------------
  // Get Refleksi dan Evaluasi Tindak Lanjut data
  // -----------------------------------------
  const {
    isLoading: isLoadingFollowUpReflectionDocument,
    data: followUpReflectionDocumentData,
  } = useAPIGetFollowUpReflectionDocument({
    params: {
      ptkId,
      tipe: 'SPNF',
    },
    periodParams: {
      skp_period: resolvedPeriodParams.skpPeriod,
      skp_year: resolvedPeriodParams.skpYear,
    },
    options: {
      useErrorBoundary: false,
      staleTime: 60 * 60 * 1000, // 1 hour
      retry: false,
    },
  })

  const isSubmitted =
    followUpReflectionDocumentData?.data?.state === 'SUBMITTED'

  // -----------------------------------------
  // Resolve Dukungan Tindak Lanjut
  // -----------------------------------------
  const validatedAdditionalSupportNeed = useMemo(() => {
    if (!followUpDocumentData) return '-'

    const additionalSupportNeed = `${followUpDocumentData?.data?.detail?.additionalSupportNeed}`

    return DUKUNGAN_UPAYA_TINDAK_LANJUT.includes(additionalSupportNeed)
      ? additionalSupportNeed
      : `Lainnya: ${additionalSupportNeed}`
  }, [followUpDocumentData])

  // -----------------------------------------
  // Other derived values
  // -----------------------------------------
  const isReadOnly = isSubmitted || isEvaluated
  const isSaveDraftDisabled =
    !formMethods.formState.isDirty ||
    upsertMutation.isLoading ||
    isLoadingFollowUpDocument ||
    isLoadingFollowUpReflectionDocument ||
    isEvaluated ||
    isReadOnly
  const isInputDisabled = isSubmitted || isEvaluated || isReadOnly

  // -----------------------------------------
  // Handle save draft
  // -----------------------------------------
  const handleClickSaveDraftButton = () => {
    setActiveDialog('SAVE_TO_DRAFT')
  }

  const handleSaveDraft = () => {
    if (isReadOnly) return

    upsertMutation.mutate(
      {
        state: 'DRAFT',
        detail: formMethods.getValues(),
        tipe: 'SPNF',
      },
      {
        onSuccess: () => {
          toast({ message: 'Berhasil simpan draf!', type: 'success' })

          handleGoToPraktikKinerjaHub()

          queryClient.invalidateQueries(API_PATH_PELAKSANAAN_PROGRESS)
          queryClient.invalidateQueries(API_GET_FOLLOW_UP_DOCUMENT_QUERY_KEY)
          queryClient.invalidateQueries(
            API_PATH_GET_FOLLOW_UP_REFLECTION_DOCUMENT
          )
        },
        onError: (err: CustomError) => {
          toast({
            message: generateErrorMessage(
              'Gagal simpan draf. Coba lagi',
              getErrorCode(err)
            ),
          })
        },
        onSettled: () => {
          setActiveDialog('NONE')
        },
      }
    )
  }

  // -----------------------------------------
  // Handle kirim
  // -----------------------------------------
  const handleClickSubmitButton = () => {
    setActiveDialog('SUBMIT')
  }

  const handleSubmit = () => {
    if (isReadOnly) return

    upsertMutation.mutate(
      {
        state: 'SUBMITTED',
        detail: formMethods.getValues(),
        tipe: 'SPNF',
      },
      {
        onSuccess: () => {
          toast({
            message: `Berhasil kirim ${STEP_TITLE}!`,
            type: 'success',
          })

          handleGoToPraktikKinerjaHub()

          queryClient.invalidateQueries(API_PATH_PELAKSANAAN_PROGRESS)
          queryClient.invalidateQueries(API_GET_FOLLOW_UP_DOCUMENT_QUERY_KEY)
          queryClient.invalidateQueries(
            API_PATH_GET_FOLLOW_UP_REFLECTION_DOCUMENT
          )
        },
        onError: (err: CustomError) => {
          toast({
            message: generateErrorMessage(
              `Gagal kirim ${STEP_TITLE}. Coba lagi`,
              getErrorCode(err)
            ),
          })
        },
        onSettled: () => {
          setActiveDialog('NONE')
        },
      }
    )
  }

  // -----------------------------------------
  // Sync form with API data
  // -----------------------------------------
  useEffect(() => {
    if (!!followUpReflectionDocumentData) {
      formMethods.reset(followUpReflectionDocumentData.data?.detail)
    }
  }, [followUpReflectionDocumentData, formMethods])

  // -----------------------------------------
  // Handle guard back navigation for unsaved changes
  // -----------------------------------------
  useBackNavigationConfirmation({
    shouldConfirm: !isSaveDraftDisabled,
    onInterceptBack: () => setActiveDialog('UNSAVED'),
  })

  // -----------------------------------------
  // Handle back navigation with fallback route
  // -----------------------------------------
  const { handleBack } = useSafeBackNavigation({
    fallbackRoute: `${PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH}/pelaksanaan/praktik-kinerja`,
  })

  return (
    <main className="pt-12 px-8 pb-8">
      <div className="mx-auto max-w-7xl flex justify-between items-center">
        <Header title={STEP_TITLE} className="mb-9" onBack={handleBack} />
      </div>

      <PageGuardPamongPelaksanaanStatus
        periodParams={resolvedPeriodParams}
        targetStep={STEP}
        unstartedRedirectRoute={PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH}
        incorrectOrderRedirectRoute={`${PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH}/pelaksanaan/praktik-kinerja`}
      >
        {isLoadingFollowUpDocument || isLoadingFollowUpReflectionDocument ? (
          <div className="flex justify-center w-full">
            <Spinner />
          </div>
        ) : (
          <form
            className="max-w-5xl mx-auto space-y-9"
            onSubmit={formMethods.handleSubmit(handleClickSubmitButton)}
          >
            <LangkahObservasiPraktikKinerjaSection
              title="Langkah-langkah:"
              steps={[
                {
                  title: 'Lakukan Refleksi',
                  description:
                    'Capaian dan tantangan yang dialami setelah tindak lanjut.',
                },
                {
                  title: 'Diskusi dengan Kepala SKB',
                  description: `Agar lebih mudah isi Evaluasi Tindak Lanjut.`,
                },
                {
                  title: 'Lakukan Upaya Perbaikan',
                  description: 'Untuk membantu Anda meningkatkan kinerja.',
                },
              ]}
            />
            <SectionCard>
              <SectionCardHeader
                variant="light-blue"
                className="flex space-x-4"
              >
                <Text variant="heading-lg" className="flex-1" as="h2">
                  {STEP_TITLE}
                </Text>
              </SectionCardHeader>

              <SectionCardBody className="py-6 space-y-4 px-9">
                <div className="space-y-4">
                  <div className="flex items-center space-x-4">
                    <NextImage
                      src="/illustrations/img-assessment-subject-book.png"
                      width="64"
                      height="64"
                    />
                    <Text variant="heading-md" className="flex-1" as="h3">
                      Pilihan Belajar
                    </Text>
                  </div>
                  <div className="flex items-start space-x-9">
                    <div className="flex-grow-0 flex-shrink-0 max-w-full p-3 rounded bg-surface-subdued w-80">
                      <dl>
                        <Text as="dt" variant="body-sm-bold">
                          Tujuan Tindak Lanjut
                        </Text>
                        <Text
                          as="dd"
                          variant="body-sm"
                          className="mb-6 line-clamp-3 overflow-wrap-anywhere"
                        >
                          {followUpDocumentData?.data?.detail
                            ?.additionalFollowUpTarget || '-'}
                        </Text>

                        <Text as="dt" variant="body-sm-bold">
                          Upaya Tindak Lanjut
                        </Text>
                        <Text
                          as="dd"
                          variant="body-sm"
                          className="mb-6 line-clamp-3 overflow-wrap-anywhere"
                        >
                          {followUpDocumentData?.data?.detail
                            ?.additionalFollowUpEffort || '-'}
                        </Text>

                        <Text as="dt" variant="body-sm-bold">
                          Periode Tindak Lanjut
                        </Text>
                        <Text
                          as="dd"
                          variant="body-sm"
                          className="mb-6 line-clamp-3 overflow-wrap-anywhere"
                        >
                          {followUpDocumentData?.data?.detail
                            ?.additionalFollowUpPeriod || '-'}
                        </Text>

                        <Text as="dt" variant="body-sm-bold">
                          Kebutuhan Dukungan (Apa/Siapa)
                        </Text>
                        <Text
                          as="dd"
                          variant="body-sm"
                          className="mb-6 line-clamp-3 overflow-wrap-anywhere"
                        >
                          {validatedAdditionalSupportNeed}
                        </Text>
                      </dl>
                      {!isReadOnly && (
                        <Button
                          size="sm"
                          color="white"
                          aria-haspopup={true}
                          aria-expanded={activeDialog === 'BELAJAR_LAINNYA'}
                          aria-label="Baca selengkapnya Pilihan Belajar"
                          onClick={() => setActiveDialog('BELAJAR_LAINNYA')}
                        >
                          Selengkapnya <Icon>chevron_right</Icon>
                        </Button>
                      )}
                    </div>
                    <div className="w-full space-y-4">
                      <fieldset className="space-y-4">
                        <Text variant="heading-sm">
                          {
                            REFLEKSI_EVALUASI_TINDAK_LANJUT_CONTENT
                              .additionalInspiration?.question
                          }
                        </Text>
                        <div className="space-y-1">
                          <Text
                            as="label"
                            htmlFor="additionalInspiration"
                            color="subdued"
                            aria-hidden={isReadOnly}
                          >
                            {
                              REFLEKSI_EVALUASI_TINDAK_LANJUT_CONTENT
                                .additionalInspiration?.label
                            }
                          </Text>
                          {isReadOnly ? (
                            <>
                              <Text
                                className="overflow-wrap-anywhere"
                                aria-hidden="true"
                              >
                                {formMethods.getValues('additionalInspiration')}
                              </Text>
                              <span className="sr-only">
                                {`${
                                  REFLEKSI_EVALUASI_TINDAK_LANJUT_CONTENT
                                    .additionalInspiration?.label
                                }: ${formMethods.getValues(
                                  'additionalInspiration'
                                )}`}
                              </span>
                            </>
                          ) : (
                            <Textarea
                              {...formMethods.register(
                                'additionalInspiration',
                                {
                                  required: {
                                    value: true,
                                    message: 'Wajib diisi',
                                  },
                                  maxLength: 500,
                                }
                              )}
                              isDisabled={isInputDisabled}
                              id="additionalInspiration"
                              placeholder="Ketik di sini"
                              maxLength={500}
                              isInvalid={
                                !!formMethods.formState.errors[
                                  'additionalInspiration'
                                ]
                              }
                              errorMessage={
                                formMethods.formState.errors[
                                  'additionalInspiration'
                                ]?.message
                              }
                            />
                          )}
                        </div>
                      </fieldset>
                      <fieldset className="space-y-4">
                        <Text variant="heading-sm">
                          {
                            REFLEKSI_EVALUASI_TINDAK_LANJUT_CONTENT
                              .additionalAchievement?.question
                          }
                        </Text>
                        <div className="space-y-1">
                          <Text
                            as="label"
                            htmlFor="additionalAchievement"
                            color="subdued"
                            aria-hidden={isReadOnly}
                          >
                            {
                              REFLEKSI_EVALUASI_TINDAK_LANJUT_CONTENT
                                .additionalAchievement?.label
                            }
                          </Text>
                          {isReadOnly ? (
                            <>
                              <Text
                                className="overflow-wrap-anywhere"
                                aria-hidden="true"
                              >
                                {formMethods.getValues('additionalAchievement')}
                              </Text>
                              <span className="sr-only">
                                {`${
                                  REFLEKSI_EVALUASI_TINDAK_LANJUT_CONTENT
                                    .additionalAchievement?.label
                                }: ${formMethods.getValues(
                                  'additionalAchievement'
                                )}`}
                              </span>
                            </>
                          ) : (
                            <Textarea
                              {...formMethods.register(
                                'additionalAchievement',
                                {
                                  required: {
                                    value: true,
                                    message: 'Wajib diisi',
                                  },
                                  maxLength: 500,
                                }
                              )}
                              isDisabled={isInputDisabled}
                              id="additionalAchievement"
                              className="w-full"
                              placeholder="Ketik di sini"
                              maxLength={500}
                              isInvalid={
                                !!formMethods.formState.errors[
                                  'additionalAchievement'
                                ]
                              }
                              errorMessage={
                                formMethods.formState.errors[
                                  'additionalAchievement'
                                ]?.message
                              }
                            />
                          )}
                        </div>
                      </fieldset>
                      <fieldset className="space-y-4">
                        <Text variant="heading-sm">
                          {
                            REFLEKSI_EVALUASI_TINDAK_LANJUT_CONTENT
                              .additionalChallenge?.question
                          }
                        </Text>
                        <div className="space-y-1">
                          <Text
                            as="label"
                            htmlFor="additionalChallenge"
                            color="subdued"
                            aria-hidden={isReadOnly}
                          >
                            {
                              REFLEKSI_EVALUASI_TINDAK_LANJUT_CONTENT
                                .additionalChallenge?.label
                            }
                          </Text>
                          {isReadOnly ? (
                            <>
                              <Text
                                className="overflow-wrap-anywhere"
                                aria-hidden="true"
                              >
                                {formMethods.getValues('additionalChallenge')}
                              </Text>
                              <span className="sr-only">
                                {`${
                                  REFLEKSI_EVALUASI_TINDAK_LANJUT_CONTENT
                                    .additionalChallenge?.label
                                }: ${formMethods.getValues(
                                  'additionalChallenge'
                                )}`}
                              </span>
                            </>
                          ) : (
                            <Textarea
                              {...formMethods.register('additionalChallenge', {
                                required: {
                                  value: true,
                                  message: 'Wajib diisi',
                                },
                                maxLength: 500,
                              })}
                              isDisabled={isInputDisabled}
                              id="additionalChallenge"
                              placeholder="Ketik di sini"
                              maxLength={500}
                              isInvalid={
                                !!formMethods.formState.errors[
                                  'additionalChallenge'
                                ]
                              }
                              errorMessage={
                                formMethods.formState.errors[
                                  'additionalChallenge'
                                ]?.message
                              }
                            />
                          )}
                        </div>
                      </fieldset>
                      <fieldset className="space-y-4">
                        <Text variant="heading-sm">
                          {
                            REFLEKSI_EVALUASI_TINDAK_LANJUT_CONTENT
                              .additionalImprovementPlan?.question
                          }
                        </Text>
                        <div className="space-y-1">
                          <Text
                            as="label"
                            htmlFor="additionalImprovementPlan"
                            color="subdued"
                            aria-hidden={isReadOnly}
                          >
                            {
                              REFLEKSI_EVALUASI_TINDAK_LANJUT_CONTENT
                                .additionalImprovementPlan?.label
                            }
                          </Text>
                          {isReadOnly ? (
                            <>
                              <Text
                                className="overflow-wrap-anywhere"
                                aria-hidden="true"
                              >
                                {formMethods.getValues(
                                  'additionalImprovementPlan'
                                )}
                              </Text>
                              <span className="sr-only">
                                {`${
                                  REFLEKSI_EVALUASI_TINDAK_LANJUT_CONTENT
                                    .additionalImprovementPlan?.label
                                }: ${formMethods.getValues(
                                  'additionalImprovementPlan'
                                )}`}
                              </span>
                            </>
                          ) : (
                            <Textarea
                              {...formMethods.register(
                                'additionalImprovementPlan',
                                {
                                  required: {
                                    value: true,
                                    message: 'Wajib diisi',
                                  },
                                  maxLength: 500,
                                }
                              )}
                              isDisabled={isInputDisabled}
                              id="additionalImprovementPlan"
                              placeholder="Ketik di sini"
                              maxLength={500}
                              isInvalid={
                                !!formMethods.formState.errors[
                                  'additionalImprovementPlan'
                                ]
                              }
                              errorMessage={
                                formMethods.formState.errors[
                                  'additionalImprovementPlan'
                                ]?.message
                              }
                            />
                          )}
                        </div>
                      </fieldset>
                    </div>
                  </div>
                </div>
              </SectionCardBody>

              {!isInputDisabled && (
                <SectionCardFooter className="justify-end px-6 py-6 space-x-3">
                  <Button
                    size="lg"
                    color="white"
                    type="button"
                    disabled={!formMethods.formState.isDirty}
                    onClick={handleClickSaveDraftButton}
                  >
                    Simpan draf
                  </Button>
                  <Button size="lg" color="blue" type="submit">
                    Kirim
                  </Button>
                </SectionCardFooter>
              )}
            </SectionCard>

            <DetailDialog
              followUpEffort={
                followUpDocumentData?.data?.detail?.additionalFollowUpEffort
              }
              followUpPeriod={
                followUpDocumentData?.data?.detail?.additionalFollowUpPeriod
              }
              followUpTarget={
                followUpDocumentData?.data?.detail?.additionalFollowUpTarget
              }
              isOpen={activeDialog === 'BELAJAR_LAINNYA'}
              supportNeed={validatedAdditionalSupportNeed}
              title="Pilihan Belajar"
              onClose={handleCloseDialog}
            />

            <DetailDialog
              followUpTargets={
                followUpDocumentData?.data?.detail?.followUpTargets
              }
              isOpen={activeDialog === 'BELAJAR_RGTK'}
              supportNeed={
                followUpDocumentData?.data?.detail?.supportNeed || '-'
              }
              title="Pilihan Belajar dari Ruang GTK"
              onClose={handleCloseDialog}
            />

            <ConfirmationDialog
              title={`Kumpulkan ${STEP_TITLE}?`}
              description="Anda dapat ubah isi dokumen jika diperlukan, selama belum dinilai oleh Kepala SKB."
              confirmCta="Konfirmasi"
              cancelCta="Kembali"
              topContent={
                <img
                  src={assetUrl('/illustrations/img-publish-data.png')}
                  alt=""
                  height={84}
                  width={84}
                />
              }
              isOpen={activeDialog === 'SUBMIT'}
              isLoading={upsertMutation.isLoading}
              onCancel={handleCloseDialog}
              onClose={handleCloseDialog}
              onConfirm={handleSubmit}
            />
            <ConfirmationDialog
              title="Simpan draf sebelum keluar?"
              description="Pilihan Anda akan disimpan dan bisa dilanjutkan nanti. Jika lewat batas waktu, draf akan otomatis dikumpulkan."
              confirmCta="Simpan dan keluar"
              cancelCta="Kembali"
              isOpen={activeDialog === 'SAVE_TO_DRAFT'}
              isLoading={upsertMutation.isLoading}
              onCancel={handleCloseDialog}
              onClose={handleCloseDialog}
              onConfirm={handleSaveDraft}
            />
            <ConfirmationDialog
              title="Perubahan belum disimpan"
              description="Mohon simpan perubahan terlebih dahulu agar dapat dilanjutkan nanti."
              confirmCta="Simpan"
              cancelCta="Lanjut keluar"
              isOpen={activeDialog === 'UNSAVED'}
              isLoading={upsertMutation.isLoading}
              onCancel={handleGoToPraktikKinerjaHub}
              onClose={handleCloseDialog}
              onConfirm={handleSaveDraft}
            />
          </form>
        )}
      </PageGuardPamongPelaksanaanStatus>
    </main>
  )
}

type DetailDialogProps = Omit<DialogProps, 'children'> & {
  title: string
  followUpEffort?: string
  followUpPeriod?: string
  followUpTarget?: string
  followUpTargets?: Array<IFollowUpTargetItem>
  supportNeed: string
}

export function DetailDialog({
  followUpEffort,
  followUpPeriod,
  followUpTarget,
  followUpTargets,
  supportNeed,
  title,
  ...dialogProps
}: DetailDialogProps) {
  return (
    <Dialog size="md" {...dialogProps}>
      <DialogOverlay />
      <DialogContent>
        <DialogHeader title={title} />

        <div className="flex flex-col p-6 space-y-4">
          <dl className="border rounded border-disabled">
            {followUpTargets && followUpTargets.length > 0 ? (
              <div className="p-3 border-b border-disabled last:border-0">
                <Text as="dt">Tujuan Tindak Lanjut</Text>
                <dd className="mt-2">
                  <ul className="list-disc pl-4">
                    {followUpTargets.map((followUpTarget) => (
                      <li key={followUpTarget.id}>
                        <Text
                          className="overflow-wrap-anywhere"
                          color="subdued"
                          variant="body-sm"
                        >
                          {followUpTarget.subIndicator}
                        </Text>
                      </li>
                    ))}
                  </ul>
                </dd>
              </div>
            ) : (
              followUpTarget && (
                <div className="p-3 border-b border-disabled last:border-0">
                  <Text as="dt">Tujuan Tindak Lanjut</Text>
                  <Text
                    as="dd"
                    className="overflow-wrap-anywhere"
                    color="subdued"
                    variant="body-sm"
                  >
                    {followUpTarget || '-'}
                  </Text>
                </div>
              )
            )}
            {followUpEffort && (
              <div className="p-3 border-b border-disabled last:border-0">
                <Text as="dt">Upaya Tindak Lanjut</Text>
                <Text
                  as="dd"
                  className="overflow-wrap-anywhere"
                  color="subdued"
                  variant="body-sm"
                >
                  {followUpEffort || '-'}
                </Text>
              </div>
            )}
            {followUpPeriod && (
              <div className="p-3 border-b border-disabled last:border-0">
                <Text as="dt">Periode Tindak Lanjut</Text>
                <Text
                  as="dd"
                  className="overflow-wrap-anywhere"
                  color="subdued"
                  variant="body-sm"
                >
                  {followUpPeriod}
                </Text>
              </div>
            )}
            <div className="p-3 border-b border-disabled last:border-0">
              <Text as="dt">Kebutuhan Dukungan (Apa/Siapa)</Text>
              <Text
                as="dd"
                className="overflow-wrap-anywhere"
                color="subdued"
                variant="body-sm"
              >
                {supportNeed || '-'}
              </Text>
            </div>
          </dl>

          <div className="flex items-center justify-center">
            <Button onClick={() => dialogProps?.onClose?.()}>Kembali</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
